import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { dataUser } from '../Helpers/util';
import { toast } from 'react-toastify';
import { MultiSelect } from 'react-multi-select-component';
import dayjs from 'dayjs';
import axiosInstance from '../../utils/axiosInstance';

import 'antd/dist/reset.css';
import SwSelect from '../Common/SwReactSelect/SwReactSelect';
import SwMultiSelect from '../Common/SwMultiSelect/SwMultiSelect';
import SwOffCanvas from '../Common/SwOffCanvas/SwOffCanvas';
import AntDDatePicker from '../Common/AntDDateTimePicker/AntDDatePicker';
import SwButton from '../Common/SwButtons/SwButtons';

const userDataLocal = dataUser();
const UserObj = JSON.parse(userDataLocal);

const RFIDExport = ({ show, onClose }) => {
  const [locations, setLocations] = useState([]);
  const [selectedLocations, setSelectedLocations] = useState([]);
  const [filters, setFilters] = useState({
    startDate: null,
    endDate: null,
  });
  const [errors, setErrors] = useState({});
  const [loadingExport, setLoadingExport] = useState(false);
  const [isCustomModalVisible, setIsCustomModalVisible] = useState(false);
  const [customDate, setCustomDate] = useState({
    startDate: null,
    endDate: null,
    selectedLocations: [],
    accessType: 'All',
    selectedDevices: [],
  });
  const [customErrors, setCustomErrors] = useState({});
  const [accessType, setAccessType] = useState('All');
  const [deviceOptions, setDeviceOptions] = useState([]);
  const [selectedDevices, setSelectedDevices] = useState([]);

  const today = dayjs();
  const threeMonthsAgoStart = today.subtract(3, 'month').startOf('month');

  useEffect(() => {
    fetchLocationsBySpoc();
  }, []);

  useEffect(() => {
    if (selectedLocations.length && accessType) {
      getDeviceName(selectedLocations, accessType);
    }
    if (customDate.selectedLocations.length && customDate.accessType) {
      getDeviceName(customDate.selectedLocations, customDate.accessType);
    }
  }, [
    selectedLocations,
    accessType,
    customDate.selectedLocations,
    customDate.accessType,
  ]);

  const handleClose = () => {
    resetValues();
    onClose();
  };

  const fetchLocationsBySpoc = async () => {
    const authToken = JSON.parse(localStorage.getItem('headData'));
    const headers = {
      Authorization: authToken?.spintlyToken,
      'Content-Type': 'application/json',
    };

    try {
      const res = await axios.get(
        `${process.env.REACT_APP_API_URL}basic-data/get-location-by-spoc`,
        {
          params: { spoc: UserObj?.rec_id, client: UserObj?.company_id },
          headers,
        },
      );

      if (res.data.status === 'success') {
        setLocations(
          res.data.data.map(location => ({
            label: location.location_name,
            value: location.rec_id,
          })),
        );
      }
    } catch (err) {
      console.error('Error fetching locations:', err);
    }
  };

  const getDeviceName = async (locations, accessType) => {
    try {
      const selectedLocationIds = locations.map(loc => loc.value).join(',');
      const res = await axiosInstance.get(
        `${
          process.env.REACT_APP_API_GATEWAYS_URL
        }access-control/v3/api/device/get-device-data?company_id=${
          UserObj.company_id
        }&location=${selectedLocationIds}&deviceFor=${accessType}`,
      );

      if (res.data.statusValue === 200) {
        setDeviceOptions(
          res.data.data.map(device => ({
            label: device.deviceName,
            value: device._id,
          })),
        );
      }
    } catch (error) {
      console.error('Error fetching device names:', error);
    }
  };

  const handleDeviceSelectionChange = selectedOptions => {
    setSelectedDevices(selectedOptions.map(device => device.value));
    if (selectedOptions.length > 0) {
      setErrors(prev => ({ ...prev, deviceName: '' }));
    }
  };

  const handleSelectionChange = selectedOptions => {
    setSelectedLocations(selectedOptions);
    setDeviceOptions([]);
    setSelectedDevices([]);
    if (selectedOptions.length > 0) {
      setErrors(prev => ({ ...prev, location: '' }));
    }
  };

  const handleInputChange = (field, value) => {
    const dateValue = value ? dayjs(value) : null;
    if (field === 'startDate') {
      setFilters(prev => ({
        ...prev,
        startDate: dateValue,
        endDate:
          prev.endDate && dateValue && prev.endDate.isBefore(dateValue, 'day')
            ? null
            : prev.endDate,
      }));
      if (dateValue) {
        setErrors(prev => ({ ...prev, startDate: '' }));
      }
    } else {
      setFilters(prev => ({ ...prev, endDate: dateValue }));
      if (dateValue) {
        setErrors(prev => ({ ...prev, endDate: '' }));
      }
    }
  };

  const validateForm = (startDate, endDate, locations, devices) => {
    let validationErrors = {};
    if (locations.length === 0)
      validationErrors.location = 'Please select at least one location';
    if (devices.length === 0)
      validationErrors.deviceName = 'Please select at least one device';
    if (!startDate) validationErrors.startDate = 'Please choose start date';
    if (!endDate) validationErrors.endDate = 'Please choose end date';
    else if (startDate && endDate && endDate.isBefore(startDate, 'day'))
      validationErrors.endDate = 'End date must be on or after start date';
    else if (
      !isCustomModalVisible &&
      startDate &&
      endDate &&
      endDate.diff(startDate, 'day') > 31
    )
      validationErrors.endDate = 'Date range should not exceed 1 month';
    return validationErrors;
  };

  const resetValues = () => {
    setSelectedLocations([]);
    setSelectedDevices([]);
    setDeviceOptions([]);
    setAccessType('All');
    setFilters({ startDate: null, endDate: null });
    setErrors({});
    setCustomDate({
      startDate: null,
      endDate: null,
      selectedLocations: [],
      accessType: 'All',
      selectedDevices: [],
    });
    setCustomErrors({});
  };

  const handleExport = async (e, isCustom = false) => {
    e.preventDefault();

    const startDate = isCustom ? customDate.startDate : filters.startDate;
    const endDate = isCustom ? customDate.endDate : filters.endDate;
    const locations = isCustom
      ? customDate.selectedLocations
      : selectedLocations;
    const devices = isCustom ? customDate.selectedDevices : selectedDevices;
    const currentAccessType = isCustom ? customDate.accessType : accessType;

    const validation = validateForm(startDate, endDate, locations, devices);

    if (Object.keys(validation).length > 0) {
      isCustom ? setCustomErrors(validation) : setErrors(validation);
      return;
    }

    setLoadingExport(true);

    try {
      const locationIds = locations.map(loc => loc.value);
      const deviceIds = devices;
      const email = UserObj.email;
      const authToken = JSON.parse(localStorage.getItem('headData'));

      if (isCustom) {
        const payload = {
          from_date: startDate.format('YYYY-MM-DD'),
          to_date: endDate.format('YYYY-MM-DD'),
          reportName: 'RFID_Logs',
          location_id: JSON.stringify(locationIds.map(id => id.toString())),
          device_id: JSON.stringify(deviceIds.map(id => id.toString())),
          access_type: JSON.stringify([currentAccessType]),
          email: email,
          company_id: UserObj?.company_id.toString(),
        };

        const response = await axiosInstance.post(
          `${
            process.env.REACT_APP_API_GATEWAYS_URL
          }cspoc/api/v2/basic-data/request-report-to-download`,
          payload,
          {
            headers: {
              Authorization: `Bearer ${authToken?.spintlyToken}`,
              'Content-Type': 'application/json',
            },
          },
        );

        if (response.status === 200) {
          toast.success(
            'Custom RFID logs export request sent. Please check your inbox.',
          );
          resetValues();
          onClose();
        }
      } else {
        const apiUrl = `${process.env.REACT_APP_API_GATEWAYS_URL}
reports/rfid-logs/getReport
?location_id=${encodeURIComponent(JSON.stringify(locationIds))}
&from_date=${startDate.format('YYYY-MM-DD')}
&to_date=${endDate.format('YYYY-MM-DD')}
&email=${email}
&device_id=${encodeURIComponent(JSON.stringify(deviceIds))}
&access_type=${encodeURIComponent(JSON.stringify([currentAccessType]))}`;

        const response = await axiosInstance.post(apiUrl);
        if (response.status === 200) {
          toast.success('RFID Logs data will be sent to your inbox shortly');
          resetValues();
          onClose();
        }
      }
    } catch (error) {
      console.error('Error exporting RFID data:', error);
      toast.error('Failed to export RFID data');
    } finally {
      setLoadingExport(false);
    }
  };

  const disabledDate = current => {
    return (
      current &&
      (current < threeMonthsAgoStart || current > today.endOf('day'))
    );
  };

  const disabledEndDate = current => {
    if (!current) return false;
    if (disabledDate(current)) return true;
    if (filters.startDate) {
      if (current.isBefore(filters.startDate, 'day')) return true;
      // Same calendar month as Start Date (matches Access Log Export)
      if (
        current.month() !== filters.startDate.month() ||
        current.year() !== filters.startDate.year()
      ) {
        return true;
      }
    }
    return false;
  };

  const accessTypeOptions = [
    { value: 'All', label: 'All' },
    { value: 'Building', label: 'Building' },
    { value: 'Company', label: 'Company' },
  ];

  return (
    <>
      <SwOffCanvas
        show={show}
        onClose={handleClose}
        title="Export RFID Logs"
        placement="end"
        footer={
          <div className="d-flex justify-content-end align-items-center gap-2">
            <SwButton variant="dark-outline" onClick={resetValues}>
              Reset
            </SwButton>

            <SwButton
              variant="primary"
              onClick={e => handleExport(e, false)}
              disabled={loadingExport}
            >
              {loadingExport ? 'Exporting...' : 'Export'}
            </SwButton>
          </div>
        }
      >
        <div className="SwFormGroup">
          <label className="SwFormLabel">
            Location Name <span className="Required">*</span>
          </label>
          <SwMultiSelect
            options={locations}
            value={selectedLocations}
            onChange={handleSelectionChange}
            labelledBy="Select"
          />
          <span className="SwTextDanger SwTextSmall">{errors.location}</span>
        </div>

        <div className="SwFormGroup">
          <label className="SwFormLabel">
            Access Type <span className="Required">*</span>
          </label>
          <SwSelect
            placeholder="Select Access type"
            options={accessTypeOptions}
            isClearable={false}
            value={accessTypeOptions.find(opt => opt.value === accessType)}
            onChange={selectedOption => {
              setAccessType(selectedOption ? selectedOption.value : 'All');
            }}
          />
        </div>

        <div className="SwFormGroup">
          <label className="SwFormLabel">
            Start Date <span className="Required">*</span>
          </label>
          <AntDDatePicker
            placeholder="Select Start Date"
            value={filters.startDate}
            onChange={date => handleInputChange('startDate', date)}
            format="DD/MM/YYYY"
            disabledDate={disabledDate}
          />
          <span className="SwTextDanger SwTextSmall">{errors.startDate}</span>
        </div>

        <div className="SwFormGroup">
          <label className="SwFormLabel">
            End Date <span className="Required">*</span>
          </label>
          <AntDDatePicker
            placeholder="Select End Date"
            value={filters.endDate}
            onChange={date => handleInputChange('endDate', date)}
            format="DD/MM/YYYY"
            disabledDate={disabledEndDate}
          />
          <span className="SwTextDanger SwTextSmall">{errors.endDate}</span>
        </div>

        <div className="SwFormGroup">
          <label className="SwFormLabel">
            Device Name <span className="Required">*</span>
          </label>
          <SwMultiSelect
            options={deviceOptions}
            value={deviceOptions.filter(device =>
              selectedDevices.includes(device.value),
            )}
            onChange={handleDeviceSelectionChange}
            labelledBy="DeviceName"
          />
          <span className="SwTextDanger SwTextSmall">{errors.deviceName}</span>
        </div>

     
      </SwOffCanvas>
    </>
  );
};

export default RFIDExport;
